<script lang="ts">
    import {
        StructuredList,
        StructuredListHead,
        StructuredListRow,
        StructuredListCell,
        StructuredListBody,
        Checkbox,
        Slider,
    } from "carbon-components-svelte";
    import AccessDisplay from './AccessDisplay.svelte'
    import Chip, {Set, Text as ChipText, LeadingIcon as ChipIcon} from '@smui/chips';
    import AccessField from './AccessField.svelte';
    import Dialog, {Title as DialogTitle, Content as DialogContent, Actions as DialogActions, InitialFocus as DialogInitialFocus} from '@smui/dialog';
    import Button, {Label as ButtonLabel} from '@smui/button';
    import Dropdown from "./Dropdown.svelte";
    import stringify from 'fast-json-stable-stringify'


    import type {Rights, StreamerRights} from './jmdb'
    import {Access, streamer_list, streamer_groups, diffDict, diffArray} from './jmdb'

    export let rights: Rights
    export let dirty: boolean = false
    export let disabled: boolean = false
    
    if (!rights.lprot) {
        rights.lprot = {override: false, opacity: 0}
    }

    export const addModifications = (tgtBundle: any, oldRights: Rights) => {
        let actorRightChanges = diffDict(oldRights.streamers, rights.streamers)
        let actorGroupRightChanges = diffDict(oldRights.streamerGroups, rights.streamerGroups)

        tgtBundle.streamerChanges = actorRightChanges
        tgtBundle.streamerGroupChanges = actorGroupRightChanges
        if (stringify(oldRights.lprot) != stringify(rights.lprot)) {
            tgtBundle.lprot = rights.lprot
        }
    }

    let editDialogOpen = false
    streamer_list.fetch()

    const skStreamer = '0'
    const skGroup = '1'

    let validId = function(id: any): boolean {
        if (id == undefined || id == null || id < 0 || id == '' || id == "undefined") {
            return false
        }
        return true
    }
    
    interface EditAccess {
        id: Number | undefined
        kind: string
        rights: StreamerRights
    }

    let oldSpecialAccess : EditAccess
    let editFormSpecialAccess : EditAccess


    let removeStreamerOrGroup = function(kind: string, id: number) {
        if (kind == skStreamer) {
            delete rights.streamers[id]
            rights.streamers = rights.streamers
        } else {
            delete rights.streamerGroups[id]
            rights.streamerGroups = rights.streamerGroups
        }
    }

    let editSpecialAccess = function(old: EditAccess) {
        oldSpecialAccess = old
        editFormSpecialAccess = JSON.parse(JSON.stringify(old))
        editDialogOpen = true
    }
    let commitSpecialAction = function() {
        // If nonhomogenous, create a new one if modification is requested
        if (oldSpecialAccess.id != null) {
            removeStreamerOrGroup(oldSpecialAccess.kind, Number(oldSpecialAccess.id))
        }
        if (editFormSpecialAccess.id != null) {
            if (editFormSpecialAccess.kind == skStreamer) {
                rights.streamers[Number(editFormSpecialAccess.id)] = editFormSpecialAccess.rights
                rights.streamers = rights.streamers
            } else {
                rights.streamerGroups[Number(editFormSpecialAccess.id)] = editFormSpecialAccess.rights
                rights.streamerGroups = rights.streamerGroups
            }
        }
        rights = rights
    }
    let deleteSpecialAction = function() {
        editFormSpecialAccess.id = undefined
        commitSpecialAction()
    }
    let specialActionDialogClosed = (action: any) => { 
        let m: {[i: string]: ()=>void} = {"save": commitSpecialAction, "delete": deleteSpecialAction, "close": () => {}}
        if (action != "close") {
            dirty = true
        }
        m[action]() 
    }


</script>

<style>
    .ph {
        position: absolute;
        font-size: calc(450px/5);
        aspect-ratio: 1280/720;
        height: 300px;
        align-items: center;
        display: flex;
        justify-content: center;
        font-family: monospace;
    }

</style>



<Dialog bind:open={editDialogOpen}  >
    {#if editFormSpecialAccess}
        
    <DialogTitle>Edit permissions</DialogTitle>
    <DialogContent>
        <div>
            <Dropdown titleText="Kind" bind:selectedVal={editFormSpecialAccess.kind}
            on:select={() => {editFormSpecialAccess.id = undefined;}}
            items={[
                {id: skStreamer.toString(), val: skStreamer, text: "Streamer"},
                {id: skGroup.toString(), val: skGroup, text: "Streamer group"}
            ]}/>
        </div>
        {#if editFormSpecialAccess.kind == skStreamer && $streamer_list.ready}
        <div>
            <Dropdown titleText="Streamer" bind:selectedVal={editFormSpecialAccess.id}
            invalid={!validId(editFormSpecialAccess.id)}
            items={$streamer_list.data.map(v=>({id: v.id.toString(), val: v.id, text: v.name}))}/>
        </div>
        {:else if $streamer_groups.ready}  
        <div>
            <Dropdown titleText="Streamer group" bind:selectedVal={editFormSpecialAccess.id}
                invalid={!validId(editFormSpecialAccess.id)}
                items={$streamer_groups.data.map((v)=>({id: v.id.toString(), val: v.id, text: v.name}))}/>
        </div>
        {/if}
        <div>
            <AccessField label="View" bind:value={editFormSpecialAccess.rights.view}/>
        </div>
        <div>
            <AccessField label="Change settings" bind:value={editFormSpecialAccess.rights.changeSettings}/>
        </div>
        <div>
            <AccessField label="View archive" bind:value={editFormSpecialAccess.rights.viewArchive}/>
        </div>

        
        
    </DialogContent>
    <DialogActions>
        <Button disabled={!validId(editFormSpecialAccess.id)} action="save" on:click={() => specialActionDialogClosed("save")}>
        <ButtonLabel>Save</ButtonLabel>
        </Button>
        <Button action="close" on:click={() => specialActionDialogClosed("close")}>
        <ButtonLabel>Cancel</ButtonLabel>
        </Button>
        <Button disabled={!validId(oldSpecialAccess.id)} action="delete" on:click={() => specialActionDialogClosed("delete")}>
        <ButtonLabel>Delete</ButtonLabel>
        </Button>
    </DialogActions>
    {/if}
</Dialog>
<div>
</div>

<StructuredList>
    <StructuredListHead>
        <StructuredListRow head>
        <StructuredListCell head>Name</StructuredListCell>
        <StructuredListCell head>View Stream</StructuredListCell>
        <StructuredListCell head>Change Settings</StructuredListCell>
        <StructuredListCell head>View Archive</StructuredListCell>
        <StructuredListCell head></StructuredListCell>
        </StructuredListRow>
    </StructuredListHead>
    <StructuredListBody>
        {#each Object.keys(rights.streamers) as sr}
        <StructuredListRow>
            <StructuredListCell>{#if $streamer_list.ready}{$streamer_list.data.find((v)=>v.id == Number(sr))?.name}{:else}{sr}{/if}</StructuredListCell>
            <StructuredListCell><AccessDisplay access={rights.streamers[Number(sr)].view}/></StructuredListCell>
            <StructuredListCell><AccessDisplay access={rights.streamers[Number(sr)].changeSettings}/></StructuredListCell>
            <StructuredListCell><AccessDisplay access={rights.streamers[Number(sr)].viewArchive}/></StructuredListCell>
            <StructuredListCell><ChipIcon on:click={() => editSpecialAccess({id: Number(sr), rights: rights.streamers[Number(sr)], kind: skStreamer})} style="cursor: pointer;" class="material-icons">mode_edit</ChipIcon></StructuredListCell>
        </StructuredListRow>
        {/each}
        {#each Object.keys(rights.streamerGroups) as sr}
        <StructuredListRow>
            <StructuredListCell>{#if $streamer_groups.ready}{$streamer_groups.data.find((v)=>v.id == Number(sr))?.name}{:else}{sr}{/if}</StructuredListCell>
            <StructuredListCell><AccessDisplay access={rights.streamerGroups[Number(sr)].view}/></StructuredListCell>
            <StructuredListCell><AccessDisplay access={rights.streamerGroups[Number(sr)].changeSettings}/></StructuredListCell>
            <StructuredListCell><AccessDisplay access={rights.streamerGroups[Number(sr)].viewArchive}/></StructuredListCell>
            <StructuredListCell><ChipIcon on:click={() => editSpecialAccess({id: Number(sr), rights: rights.streamerGroups[Number(sr)], kind: skGroup})} style="cursor: pointer;" class="material-icons">mode_edit</ChipIcon></StructuredListCell>
        </StructuredListRow>
        {/each}
        <StructuredListRow>
            <StructuredListCell>
                <Button disabled={disabled} on:click={() => editSpecialAccess({id: undefined, rights: {view: Access.Inherit, viewArchive: Access.Inherit, changeSettings: Access.Inherit}, kind: skStreamer}) }><ButtonLabel>Add new</ButtonLabel></Button>
            </StructuredListCell>
            <StructuredListCell></StructuredListCell>
            <StructuredListCell></StructuredListCell>
            <StructuredListCell></StructuredListCell>
            <StructuredListCell></StructuredListCell>
        </StructuredListRow>
    </StructuredListBody>
</StructuredList>


<Checkbox disabled={disabled} labelText="Override leak protection" bind:checked={rights.lprot.override}/>
{#if rights.lprot?.override}
    <Slider bind:value={rights.lprot.opacity} max={100} maxLabel="Opaque" minLabel="Invisible" min={0}/>
    <div>
        <div class="ph" style="color: rgba(255,255,255,{rights.lprot.opacity / 100});">John Doe</div>
        <img src="/mgr/video_placeholder.jpg" style="height: 300px;"/>
    </div>
{/if}
